import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "@tanstack/react-router";
import { Lock, Check, X, Sparkles, ArrowRight, Feather } from "lucide-react";

export type LessonBlock = {
  id: string;
  sort_order: number;
  block_type: string;
  data: Record<string, unknown>;
  entitlement_slug: string | null;
};

export function LessonBlocks({ blocks, locked = false }: { blocks: LessonBlock[]; locked?: boolean }) {
  if (!blocks.length) return null;
  return (
    <div className="flex flex-col gap-6">
      {blocks.map((b) => (
        <BlockRenderer key={b.id} block={b} locked={locked && Boolean(b.entitlement_slug)} />
      ))}
    </div>
  );
}

function BlockRenderer({ block, locked }: { block: LessonBlock; locked: boolean }) {
  if (locked) {
    return (
      <div className="rounded-xl border border-gold/40 bg-gold/5 p-4 text-sm">
        <div className="flex items-center gap-2 text-gold"><Lock size={14} /> Premium content</div>
        <p className="mt-1 text-foreground/70">Upgrade to unlock this block.</p>
      </div>
    );
  }
  const d = block.data as Record<string, unknown>;
  switch (block.block_type) {
    case "markdown":
      return (
        <div className="prose prose-invert max-w-none">
          <ReactMarkdown>{(d.body as string) ?? ""}</ReactMarkdown>
        </div>
      );
    case "video":
      return <video src={d.url as string} controls className="w-full rounded-xl border hairline" poster={d.poster as string} />;
    case "audio":
      return <audio src={d.url as string} controls className="w-full" />;
    case "image":
      return <img src={d.url as string} alt={(d.alt as string) ?? ""} className="w-full rounded-xl border hairline" loading="lazy" />;
    case "embed":
      return <iframe src={d.url as string} title={(d.title as string) ?? "Embed"} loading="lazy"
        className="aspect-video w-full rounded-xl border hairline" />;
    case "download":
      return (
        <a href={d.url as string} className="inline-flex items-center gap-2 rounded-md border hairline px-3 py-2 text-sm hover:border-gold/60">
          Download {(d.label as string) ?? "file"}
        </a>
      );
    case "code":
      return <pre className="overflow-x-auto rounded-xl border hairline bg-obsidian/60 p-4 text-xs"><code>{d.body as string}</code></pre>;
    case "visualization":
      return <VisualizationBlock data={d} />;
    case "reflection":
      return <ReflectionBlock data={d} />;
    case "quiz":
      return <QuizBlock data={d} />;
    case "crosslink":
      return <CrosslinkBlock data={d} />;
    case "guided_example":
      return (
        <div className="rounded-2xl border hairline bg-gold/5 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
            <Sparkles size={14} /> Guided example
          </div>
          <div className="prose prose-invert mt-3 max-w-none">
            <ReactMarkdown>{(d.body as string) ?? ""}</ReactMarkdown>
          </div>
        </div>
      );
    case "flashcard":
    case "interactive":
      return (
        <div className="rounded-xl border hairline p-4 text-sm text-foreground/70">
          <div className="mb-1 text-xs uppercase tracking-widest text-gold">{block.block_type}</div>
          {(d.title as string) ?? "Interactive block"}
        </div>
      );
    default:
      return null;
  }
}

function VisualizationBlock({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="rounded-2xl border hairline bg-obsidian/70 p-4">
      <div className="text-xs uppercase tracking-widest text-gold">Visualization</div>
      <p className="mt-1 font-serif text-lg text-gold-luminous">
        {(data.title as string) ?? "Living diagram"}
      </p>
      {data.body ? (
        <div className="prose prose-invert mt-3 max-w-none">
          <ReactMarkdown>{data.body as string}</ReactMarkdown>
        </div>
      ) : null}
      <p className="mt-3 text-xs text-foreground/60">
        Explore the interactive map on this domain's hero to feel this concept live.
      </p>
    </div>
  );
}

function ReflectionBlock({ data }: { data: Record<string, unknown> }) {
  const prompts = (data.prompts as string[] | undefined) ?? [];
  return (
    <div className="rounded-2xl border hairline p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
        <Feather size={14} /> Reflection
      </div>
      <ul className="mt-3 space-y-2 text-sm text-foreground/85">
        {prompts.map((p, i) => <li key={i}>• {p}</li>)}
      </ul>
    </div>
  );
}

type QuizQuestion = { q: string; options: string[]; answer: number; explain?: string };

function QuizBlock({ data }: { data: Record<string, unknown> }) {
  const questions = (data.questions as QuizQuestion[] | undefined) ?? [];
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [checked, setChecked] = useState(false);
  const correct = questions.reduce((n, q, i) => n + (picked[i] === q.answer ? 1 : 0), 0);
  const passed = checked && correct === questions.length;

  if (!questions.length) return null;
  return (
    <div className="rounded-2xl border hairline p-5">
      <div className="text-xs uppercase tracking-widest text-gold">Knowledge check</div>
      <ol className="mt-3 space-y-4">
        {questions.map((q, i) => (
          <li key={i}>
            <p className="text-sm text-foreground/90">{i + 1}. {q.q}</p>
            <div className="mt-2 flex flex-col gap-1">
              {q.options.map((opt, oi) => {
                const isPicked = picked[i] === oi;
                const isRight = checked && oi === q.answer;
                const isWrong = checked && isPicked && oi !== q.answer;
                return (
                  <button
                    key={oi}
                    type="button"
                    onClick={() => setPicked({ ...picked, [i]: oi })}
                    className={"flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition " +
                      (isRight ? "border-emerald-400/70 bg-emerald-500/10 text-emerald-200"
                        : isWrong ? "border-red-400/70 bg-red-500/10 text-red-200"
                        : isPicked ? "border-gold/70 bg-gold/10" : "hairline hover:border-gold/40")}
                  >
                    <span>{opt}</span>
                    {isRight && <Check size={14} />}
                    {isWrong && <X size={14} />}
                  </button>
                );
              })}
            </div>
            {checked && q.explain && (
              <p className="mt-2 text-xs text-foreground/60">{q.explain}</p>
            )}
          </li>
        ))}
      </ol>
      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={() => setChecked(true)}
          className="rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian">
          Check answers
        </button>
        {checked && (
          <span className={passed ? "text-sm text-emerald-300" : "text-sm text-foreground/70"}>
            {correct} / {questions.length} correct{passed ? " — mastered." : ""}
          </span>
        )}
      </div>
    </div>
  );
}

type Crosslink = { domain: string; lesson: string; label: string };

function CrosslinkBlock({ data }: { data: Record<string, unknown> }) {
  const links = (data.links as Crosslink[] | undefined) ?? [];
  if (!links.length) return null;
  return (
    <div className="rounded-2xl border hairline p-5">
      <div className="text-xs uppercase tracking-widest text-gold">Continue the thread</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {links.map((l, i) => (
          <Link key={i}
            to="/academy/$domain/lesson/$lesson"
            params={{ domain: l.domain, lesson: l.lesson }}
            className="group flex items-center justify-between rounded-md border hairline px-3 py-2 text-sm hover:border-gold/60"
          >
            <span>{l.label}</span>
            <ArrowRight size={14} className="text-gold transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
}
